import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OrcamentoDTO } from '../../../models/financeiro.model';
import { FinanceiroService } from '../../../services/financeiro.service';
import { ModalStateService } from '../../../services/modaState.service';

@Component({
  selector: 'app-orcamento-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './orcamento-modal.html',
  styleUrls: ['./orcamento-modal.css'],
})
export class OrcamentoModal implements OnChanges {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<OrcamentoDTO | undefined>();

  orcamentos: OrcamentoDTO[] = [];
  filteredOrcamentos: OrcamentoDTO[] = [];
  paginatedOrcamentos: OrcamentoDTO[] = [];
  selectedOrcamento: OrcamentoDTO | null = null;
  searchTerm = '';

  currentPage = 0;
  itemsPerPage = 5;
  totalPages = 1;
  maxPagesToShow = 5;
  startPage = 0;
  endPage = 0;
  pages: number[] = [];

  private readonly financeiroService = inject(FinanceiroService);
  private readonly modalStateService = inject(ModalStateService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.loadOrcamentos();
    }
  }

  loadOrcamentos(): void {
    this.financeiroService.getOrcamentosAbertos(0, 500).subscribe({
      next: (data) => {
        this.orcamentos = data.content;
        this.filteredOrcamentos = [...data.content];
        this.selectedOrcamento = null;
        this.currentPage = 0;
        this.rebuildPagination(true);
      },
      error: () => {
        this.orcamentos = [];
        this.filteredOrcamentos = [];
        this.paginatedOrcamentos = [];
        this.totalPages = 1;
        this.pages = [];
      }
    });
  }

  search(): void {
    const term = this.searchTerm.trim().toLowerCase();

    if (!term) {
      this.filteredOrcamentos = [...this.orcamentos];
    } else {
      this.filteredOrcamentos = this.orcamentos.filter((orcamento) =>
        `${orcamento.id} ${orcamento.nomeCliente} ${orcamento.valorTotal}`.toLowerCase().includes(term)
      );
    }

    this.rebuildPagination(true);
  }

  selectOrcamento(orcamento: OrcamentoDTO): void {
    this.selectedOrcamento = orcamento;
  }

  inserirOrcamento(): void {
    if (!this.selectedOrcamento) {
      return;
    }

    this.isOpen = false;
    this.modalStateService.close(this.selectedOrcamento);
    this.closeModal.emit(this.selectedOrcamento);
  }

  onClose(): void {
    this.isOpen = false;
    this.closeModal.emit(undefined);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredOrcamentos = [...this.orcamentos];
    this.rebuildPagination(true);
  }

  setPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }

    this.currentPage = page;
    const startIndex = page * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedOrcamentos = this.filteredOrcamentos.slice(startIndex, endIndex);
    this.updatePageNumbers();
  }

  private rebuildPagination(resetPage: boolean): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredOrcamentos.length / this.itemsPerPage));

    if (this.filteredOrcamentos.length === 0) {
      this.currentPage = 0;
      this.pages = [];
      this.paginatedOrcamentos = [];
      return;
    }

    const targetPage = resetPage ? 0 : Math.min(this.currentPage, this.totalPages - 1);
    this.setPage(targetPage);
  }

  private updatePageNumbers(): void {
    const halfPages = Math.floor(this.maxPagesToShow / 2);
    this.startPage = Math.max(0, this.currentPage - halfPages);
    this.endPage = Math.min(this.totalPages - 1, this.startPage + this.maxPagesToShow - 1);

    if (this.endPage - this.startPage + 1 < this.maxPagesToShow) {
      this.startPage = Math.max(0, this.endPage - this.maxPagesToShow + 1);
    }

    this.pages = Array.from(
      { length: this.endPage - this.startPage + 1 },
      (_, index) => this.startPage + index
    );
  }

}
